import type { AlertLevel, BehavioralFlags, MonitoringResult } from '@/types';
import { activeProctorLabels } from '@/utils/proctorFlags';
import { getLatestLandmarkerHeadPose } from './faceLandmarkerHeadPose';
import {
  isHeadPoseAwayFromCamera,
  type HeadPoseDegrees,
} from './facialTransformPose';
import { readWebGazerFaceLandmarks } from './webgazerMonitor';

export interface GazeSample {
  x: number;
  y: number;
  t: number;
  missing: boolean;
}

export type GazeInput = {
  x: number;
  y: number;
  eyeLeftWidth?: number;
  eyeRightWidth?: number;
} | null;

const recentGaze: GazeSample[] = [];
const recentHeadAway: boolean[] = [];
const MAX_SAMPLES = 40;
const MAX_HEAD_SAMPLES = 20;

const EDGE_BAND_FRACTION = 0.05;
const AWAY_RATIO = 0.65;
const AWAY_MIN_SAMPLES = 10;
const FACE_MISSING_STREAK = 10;
const HEAD_AWAY_MIN_SAMPLES = 4;
const HEAD_AWAY_RATIO = 0.6;

export type FlagsFromGazeOptions = {
  /**
   * Inject head pose in tests. Live path reads the independent Face Landmarker
   * when this key is omitted.
   */
  headPose?: HeadPoseDegrees | null;
  /** When true, do not push into the gaze missing-streak window (head-pose-only tick). */
  skipGazeHistory?: boolean;
};

export function resetClientProctorState(): void {
  recentGaze.length = 0;
  recentHeadAway.length = 0;
}

export function isGazeAwayFromScreen(
  x: number,
  y: number,
  viewport: { width: number; height: number }
): boolean {
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    x < 0 ||
    y < 0 ||
    x > viewport.width ||
    y > viewport.height
  ) {
    return true;
  }

  const bandX = Math.max(12, viewport.width * EDGE_BAND_FRACTION);
  const bandY = Math.max(12, viewport.height * EDGE_BAND_FRACTION);
  return (
    x <= bandX ||
    y <= bandY ||
    x >= viewport.width - bandX ||
    y >= viewport.height - bandY
  );
}

/** @deprecated use isGazeAwayFromScreen */
export function isGazeOnScreen(
  x: number,
  y: number,
  viewport: { width: number; height: number }
): boolean {
  return !isGazeAwayFromScreen(x, y, viewport);
}

export function readFaceFeedbackTowardCamera(): boolean | null {
  if (typeof document === 'undefined') return null;
  const box = document.getElementById('webgazerFaceFeedbackBox');
  if (!box) return null;
  const border = `${box.style.border} ${box.style.borderColor}`.toLowerCase();
  if (border.includes('red')) return false;
  if (border.includes('green')) return true;
  return null;
}

function resolveHeadPose(options?: FlagsFromGazeOptions): HeadPoseDegrees | null {
  if (options && 'headPose' in options) {
    return options.headPose ?? null;
  }
  return getLatestLandmarkerHeadPose();
}

/**
 * Screen / face integrity:
 * - lookingAway: WebGazer gaze off-screen / edge
 * - noFaceDetected: sustained null gaze
 * - headMovementExcessive: independent Face Landmarker yaw/pitch (not WebGazer eyes)
 */
export function flagsFromGaze(
  gaze: GazeInput,
  viewport: { width: number; height: number } = {
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 720,
  },
  options?: FlagsFromGazeOptions
): {
  flags: BehavioralFlags;
  alertLevel: AlertLevel;
  focusPercentage: number;
  headPose: HeadPoseDegrees | null;
} {
  const now = Date.now();
  const missing = !gaze || Number.isNaN(gaze.x) || Number.isNaN(gaze.y);

  if (!options?.skipGazeHistory) {
    if (missing) {
      recentGaze.push({ x: 0, y: 0, t: now, missing: true });
    } else {
      recentGaze.push({ x: gaze!.x, y: gaze!.y, t: now, missing: false });
    }
    while (recentGaze.length > MAX_SAMPLES) recentGaze.shift();
  }

  let missingStreak = 0;
  for (let i = recentGaze.length - 1; i >= 0; i--) {
    if (!recentGaze[i].missing) break;
    missingStreak += 1;
  }

  const headPose = resolveHeadPose(options);
  const poseAway = isHeadPoseAwayFromCamera(headPose);
  const faceToward = readFaceFeedbackTowardCamera();
  const feedbackAway = faceToward === false;
  const frameAway = poseAway || feedbackAway;

  // Always arm hysteresis from landmarker samples (including "frontal" frames).
  if (headPose || faceToward !== null) {
    recentHeadAway.push(frameAway);
    while (recentHeadAway.length > MAX_HEAD_SAMPLES) recentHeadAway.shift();
  }

  let headMovementExcessive = false;
  if (recentHeadAway.length >= HEAD_AWAY_MIN_SAMPLES) {
    const windowSamples = recentHeadAway.slice(-HEAD_AWAY_MIN_SAMPLES);
    const awayCount = windowSamples.filter(Boolean).length;
    headMovementExcessive = awayCount / windowSamples.length >= HEAD_AWAY_RATIO;
  }
  if (feedbackAway || poseAway) {
    headMovementExcessive = true;
  }

  const noFaceDetected = missingStreak >= FACE_MISSING_STREAK;

  const present = recentGaze.filter((s) => !s.missing);
  let lookingAway = false;
  if (present.length >= AWAY_MIN_SAMPLES) {
    const windowSamples = present.slice(-AWAY_MIN_SAMPLES);
    const awayCount = windowSamples.filter((s) =>
      isGazeAwayFromScreen(s.x, s.y, viewport)
    ).length;
    lookingAway = awayCount / windowSamples.length >= AWAY_RATIO;
  }

  const flags: BehavioralFlags = {
    lookingAway: lookingAway && !noFaceDetected,
    multipleFaces: false,
    noFaceDetected,
    suspiciousMovement: false,
    headMovementExcessive: headMovementExcessive && !noFaceDetected,
  };

  let alertLevel: AlertLevel = 'NORMAL';
  let focusPercentage = 100;
  if (noFaceDetected) {
    alertLevel = 'CRITICAL_ALERT';
    focusPercentage = 0;
  } else if (lookingAway || headMovementExcessive) {
    alertLevel = 'DISTRACTED';
    focusPercentage = headMovementExcessive && lookingAway ? 35 : 50;
  }

  return { flags, alertLevel, focusPercentage, headPose };
}

export function monitoringResultFromGaze(
  gaze: GazeInput,
  sessionId?: string,
  options?: FlagsFromGazeOptions
): MonitoringResult {
  const { flags, alertLevel, focusPercentage, headPose } = flagsFromGaze(
    gaze,
    undefined,
    options
  );

  if (typeof window !== 'undefined') {
    const w = window as Window & { __qsPoseLogAt?: number };
    const now = Date.now();
    if (!w.__qsPoseLogAt || now - w.__qsPoseLogAt > 1500) {
      w.__qsPoseLogAt = now;
      console.info('[QuizSecure head-pose]', {
        source: 'FaceLandmarker',
        yaw: headPose?.yaw ?? null,
        pitch: headPose?.pitch ?? null,
        landmarksFallback: readWebGazerFaceLandmarks()?.length ?? 0,
        headMovementExcessive: flags.headMovementExcessive,
        lookingAway: flags.lookingAway,
        alertLevel,
      });
    }
  }

  const headPosePayload = headPose
    ? { yaw: headPose.yaw, pitch: headPose.pitch, roll: 0 }
    : undefined;

  const yaw = headPose ? headPose.yaw : null;
  const pitch = headPose ? headPose.pitch : null;

  let gazeData: MonitoringResult['gazeData'];
  if (gaze) {
    gazeData = {
      x: gaze.x,
      y: gaze.y,
      yaw,
      pitch,
      confidence: 0.7,
    };
  } else if (headPose) {
    // Head-pose-only sample so the store/overlay still update without WebGazer.
    gazeData = {
      yaw,
      pitch,
      confidence: 0.5,
    };
  } else {
    gazeData = undefined;
  }

  return {
    timestamp: Date.now(),
    alertLevel,
    alertCount: alertLevel === 'NORMAL' ? 0 : 1,
    focusPercentage,
    confidenceScore: gaze ? 0.7 : headPose ? 0.5 : 0,
    gazeData,
    headPose: headPosePayload,
    behavioralFlags: flags,
    systemUsed: 'WebGazer+FaceLandmarker',
    sessionId,
    message: activeProctorLabels(flags).join(' · ') || undefined,
  };
}
